import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { SkeletonCard } from "../components/Feedback";
import api, { formatTime, tripStatus } from "../lib/api";
import { ArrowRight, Ticket, Package, MapTrifold, Phone, ShieldCheck, Lightning, HeartStraight } from "@phosphor-icons/react";

const HERO_IMAGE = "/images/hero-chauffeur-1086.webp";
const HERO_SRCSET = "/images/hero-chauffeur-640.webp 640w, /images/hero-chauffeur-1086.webp 1086w";
const ROUTE_IMG = "/images/route-1280.webp";
const ROUTE_SRCSET = "/images/route-640.webp 640w, /images/route-1280.webp 1280w";

export default function Home() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trips?only_available=true").then((r) => setTrips(r.data.slice(0, 3))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} srcSet={HERO_SRCSET} sizes="100vw" width="1086" height="1448" fetchPriority="high" decoding="async" alt="Chauffeur ALLO DAKAR accueillant une passagère devant une Toyota Avensis" className="w-full h-full object-cover object-center" />
          {/* Gradient overlay (readability) */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3F33]/90 via-[#1E3F33]/55 to-transparent" />
          <div className="grain" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/95 text-xs font-semibold tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E69C24] animate-pulse"></span>
              SAINT-LOUIS · DAKAR · TOUS LES JOURS
            </div>
            <h1 className="mt-6 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05]">
              ALLO <span className="text-[#E69C24]">DAKAR</span>
            </h1>
            <p className="mt-4 text-white/90 text-lg sm:text-xl max-w-xl font-medium">
              Voyagez simplement, confortablement et en toute sécurité au Sénégal. Réservez votre trajet, louez un véhicule avec chauffeur ou envoyez un colis, en quelques clics.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/trajets" data-testid="cta-book-ride" className="btn-secondary text-base">
                <Ticket size={20} weight="duotone" /> Réserver un trajet
              </Link>
              <Link to="/colis" data-testid="cta-send-parcel" className="btn-outline !text-white !border-white/30 hover:!bg-white/10">
                <Package size={20} weight="duotone" /> Envoyer un colis
              </Link>
            </div>
          </div>
        </div>

        {/* trust bar */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, label: "Trajets sécurisés" },
            { icon: Lightning, label: "Réservation simple" },
            { icon: HeartStraight, label: "Service local" },
            { icon: MapTrifold, label: "Assistance WhatsApp" },
          ].map(({ icon: I, label }) => (
            <div key={label} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/95 backdrop-blur border border-white/40">
              <I size={20} weight="duotone" className="text-[#1E3F33]" />
              <span className="text-sm font-semibold text-[#1A1F1C]">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bento CTA grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline">Que souhaitez-vous faire ?</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-2 max-w-2xl">Vos déplacements au Sénégal, simplifiés.</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          <Link to="/trajets" data-testid="bento-reserver" className="col-span-2 row-span-2 card-surface relative overflow-hidden p-7 md:p-9 group min-h-[260px]">
            <div className="absolute -right-12 -bottom-12 w-56 h-56 rounded-full bg-[#1E3F33]/5 group-hover:bg-[#1E3F33]/10 transition-colors" />
            <Ticket size={36} weight="duotone" className="text-[#1E3F33]" />
            <h3 className="font-display font-bold text-2xl md:text-3xl mt-6">Réserver un trajet</h3>
            <p className="text-[#5C6B64] mt-2 max-w-xs">Choisissez votre départ, votre paiement et confirmez sur WhatsApp.</p>
            <div className="absolute bottom-6 right-6 w-12 h-12 rounded-full bg-[#1E3F33] text-white flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <ArrowRight size={20} />
            </div>
          </Link>

          <Link to="/colis" data-testid="bento-colis" className="card-surface p-6 group">
            <Package size={28} weight="duotone" className="text-[#C85A17]" />
            <h3 className="font-display font-bold text-lg mt-4">Envoyer un colis</h3>
            <p className="text-sm text-[#5C6B64] mt-1">D'une ville à l'autre.</p>
          </Link>

          <Link to="/trajets" data-testid="bento-departs" className="card-surface p-6 group">
            <MapTrifold size={28} weight="duotone" className="text-[#00A9E0]" />
            <h3 className="font-display font-bold text-lg mt-4">Voir les départs</h3>
            <p className="text-sm text-[#5C6B64] mt-1">Horaires en temps réel.</p>
          </Link>

          <Link to="/location" data-testid="bento-location" className="card-surface p-6 group col-span-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <Phone size={28} weight="duotone" className="text-[#1E3F33]" />
                <h3 className="font-display font-bold text-lg mt-4">Louer une voiture</h3>
                <p className="text-sm text-[#5C6B64] mt-1">Besoin d'un véhicule privé avec chauffeur ? Faites votre demande en quelques clics.</p>
              </div>
              <ArrowRight size={20} className="text-[#5C6B64] group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* Featured departures */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">Prochains départs</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-2">À ne pas manquer</h2>
          </div>
          <Link to="/trajets" className="text-sm font-semibold text-[#1E3F33] hover:underline" data-testid="see-all-trips">Voir tous →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && [0, 1, 2].map((i) => <SkeletonCard key={i} lines={3} />)}
          {!loading && trips.length === 0 && (
            <div className="col-span-full text-center text-[#5C6B64] py-12">Aucun départ disponible pour le moment.</div>
          )}
          {trips.map((t) => {
            const st = tripStatus(t.seats_available);
            return (
            <div key={t.id} className="card-surface p-6 flex flex-col" data-testid={`home-trip-${t.id}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                <span className="text-[#5C6B64]">{formatTime(t.time)}</span>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <div className="text-lg font-display font-bold">{t.departure_city}</div>
                <ArrowRight size={18} className="text-[#C85A17]" />
                <div className="text-lg font-display font-bold">{t.arrival_city}</div>
              </div>
              <div className="mt-4">
                <div className="text-2xl font-display font-bold text-[#1A1F1C]">{t.price?.toLocaleString("fr-FR")} <span className="text-sm font-medium text-[#5C6B64]">FCFA</span></div>
                <div className="text-[10px] text-[#5C6B64] mt-0.5">sans bagages · {t.seats_total - t.seats_available} / {t.seats_total} réservées</div>
              </div>
              <Link to={`/reserver/${t.id}`} className="btn-primary mt-5 self-start" data-testid={`home-book-${t.id}`}>Réserver</Link>
            </div>
            );
          })}
        </div>
      </section>

      {/* Highway image strip */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img src={ROUTE_IMG} srcSet={ROUTE_SRCSET} sizes="100vw" loading="lazy" decoding="async" width="1280" height="720" className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3F33]/85 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-md text-white">
              <div className="overline text-[#E69C24]">Notre engagement</div>
              <h3 className="font-display font-bold text-2xl md:text-3xl mt-2">Ponctualité. Confort. Sécurité.</h3>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
